import React, { useState } from "react";
import { motion } from "framer-motion";
import { Linkedin, Instagram, Facebook, Twitter, Mail, X, Crown } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import { useDirectors, useMedia } from "@/lib/SiteContent";
import { fadeUp } from "@/lib/motion";
import { cn } from "@/lib/utils";

const DEFAULT_PORTRAITS = [
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=800&q=85",
  "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=800&q=85",
  "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=800&q=85",
];

const SocialIcon = ({ type, url, label }) => {
  if (!url || url === "#") return null;
  const map = { linkedin: Linkedin, instagram: Instagram, facebook: Facebook, twitter: Twitter, email: Mail };
  const Icon = map[type];
  if (!Icon) return null;
  const href = type === "email" ? `mailto:${url}` : url;
  return (
    <a
      href={href}
      target={type === "email" ? "_self" : "_blank"}
      rel="noopener noreferrer"
      aria-label={label || type}
      data-testid={`director-social-${type}`}
      className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-black/40 backdrop-blur text-white/75 hover:text-[color:var(--sp-gold)] hover:border-[color:rgba(212,175,55,0.5)] transition-colors"
    >
      <Icon className="h-3.5 w-3.5" />
    </a>
  );
};

const DirectorCard = ({ director, index }) => {
  const cmsPhoto = useMedia(`director_${index + 1}`);
  // Order of preference: explicit photoUrl > admin CMS upload > default stock portrait
  const photoSrc = director.photoUrl || cmsPhoto || DEFAULT_PORTRAITS[index % DEFAULT_PORTRAITS.length];

  const social = director.social || {};
  const paragraphs = (director.longBio || "").split(/\n\s*\n/).filter(Boolean);

  return (
    <Dialog>
      <motion.div
        {...fadeUp}
        transition={{ ...fadeUp.transition, delay: 0.06 * index }}
        data-testid={`director-card-${index + 1}`}
        className="glass-card neon-border-dual rounded-[20px] overflow-hidden relative group"
      >
        {/* Portrait */}
        <div className="relative aspect-[4/5] overflow-hidden">
          <img
            src={photoSrc}
            alt={director.name}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
          <span className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full bg-black/55 backdrop-blur-md border border-[color:rgba(212,175,55,0.4)] px-3 py-1 text-[10px] tracking-[0.22em] uppercase text-[color:var(--sp-gold)]">
            <Crown className="h-3 w-3" /> Director 0{index + 1}
          </span>
        </div>

        {/* Card text */}
        <div className="p-6 md:p-7 -mt-20 relative">
          <h3 className="font-display text-2xl md:text-3xl leading-tight text-white">{director.name}</h3>
          <p className="mt-1 text-[12px] tracking-[0.2em] uppercase text-[color:var(--sp-gold)]">
            {director.role}
          </p>
          <p className="mt-4 text-sm text-white/72 leading-relaxed">{director.shortBio}</p>

          <div className="mt-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SocialIcon type="linkedin" url={social.linkedin} label="LinkedIn" />
              <SocialIcon type="instagram" url={social.instagram} label="Instagram" />
              <SocialIcon type="facebook" url={social.facebook} label="Facebook" />
              <SocialIcon type="twitter" url={social.twitter} label="X / Twitter" />
              <SocialIcon type="email" url={social.email} label="Email" />
            </div>
            <DialogTrigger asChild>
              <button
                type="button"
                data-testid={`director-view-profile-${index + 1}`}
                className="text-[11px] tracking-[0.22em] uppercase text-white/75 hover:text-[color:var(--sp-gold)] transition-colors"
              >
                View Profile →
              </button>
            </DialogTrigger>
          </div>
        </div>
      </motion.div>

      <DialogContent
        data-testid={`director-dialog-${index + 1}`}
        className="max-w-3xl w-[calc(100%-1.5rem)] sm:w-[calc(100%-3rem)] bg-[#0c0c0c] border border-white/12 text-white p-0 overflow-hidden max-h-[92vh] sm:max-h-[88vh] [&>button.absolute.right-4.top-4]:hidden"
      >
        {/* Large, always-visible custom close button (overrides Shadcn default which is hard to see on photo) */}
        <DialogClose
          data-testid={`director-dialog-close-${index + 1}`}
          aria-label="Close profile"
          className="absolute right-3 top-3 z-[60] flex h-10 w-10 items-center justify-center rounded-full bg-black/80 backdrop-blur-md border border-[color:rgba(212,175,55,0.55)] text-[color:var(--sp-gold)] shadow-[0_8px_24px_rgba(0,0,0,0.6)] hover:bg-[color:var(--sp-gold)] hover:text-black transition-colors focus:outline-none focus:ring-2 focus:ring-[color:var(--sp-gold)] focus:ring-offset-2 focus:ring-offset-[#0c0c0c]"
        >
          <X className="h-5 w-5" />
          <span className="sr-only">Close</span>
        </DialogClose>

        <div className="grid md:grid-cols-5 gap-0 overflow-y-auto max-h-[92vh] sm:max-h-[88vh]">
          {/* Image: capped on mobile so face is visible and content shows below */}
          <div className="md:col-span-2 relative h-72 sm:h-80 md:h-auto md:min-h-[420px] overflow-hidden bg-black">
            <img
              src={photoSrc}
              alt={director.name}
              className="absolute inset-0 h-full w-full object-cover md:object-center"
              style={{ objectPosition: "center 22%" }}
            />
            <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-l from-transparent via-transparent to-[#0c0c0c]/60 md:to-[#0c0c0c]/40" />
            {/* Name overlay visible on mobile (under cropped photo for context) */}
            <div className="absolute inset-x-0 bottom-0 p-4 md:hidden bg-gradient-to-t from-[#0c0c0c] via-[#0c0c0c]/85 to-transparent">
              <p className="text-[10px] tracking-[0.28em] uppercase text-[color:var(--sp-gold)]">
                {director.role}
              </p>
              <h2 className="font-display text-2xl mt-1 leading-tight">
                {director.name}
              </h2>
            </div>
          </div>
          <div className="md:col-span-3 p-6 md:p-9">
            <p className="hidden md:block text-[10px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
              <span className="gold-divider" /> Leadership profile
            </p>
            <h2 className="hidden md:block font-display text-3xl md:text-4xl mt-3 leading-tight">
              {director.name}
            </h2>
            <p className="hidden md:block mt-1 text-[12px] tracking-[0.22em] uppercase text-white/65">
              {director.role}
            </p>
            <div className="md:mt-5 space-y-4 text-white/78 text-[15px] leading-[1.75]">
              {paragraphs.length > 0 ? (
                paragraphs.map((p, i) => (
                  <p key={`${director.id || director.name}-bio-${i}`}>{p}</p>
                ))
              ) : (
                <p>{director.shortBio}</p>
              )}
            </div>
            <div className="mt-7 pt-5 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <SocialIcon type="linkedin" url={social.linkedin} label="LinkedIn" />
                <SocialIcon type="instagram" url={social.instagram} label="Instagram" />
                <SocialIcon type="facebook" url={social.facebook} label="Facebook" />
                <SocialIcon type="twitter" url={social.twitter} label="X / Twitter" />
                <SocialIcon type="email" url={social.email} label="Email" />
              </div>
              <p className="text-[10px] tracking-[0.28em] uppercase text-white/45">
                Seetai Properties — Board
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DirectorsSection = () => {
  const directors = useDirectors();
  const visible = (directors || []).filter((d) => d.isActive !== false).slice(0, 6);
  if (!visible.length) return null;
  return (
    <section data-testid="about-directors" className="luxe-section bg-[#0a0a0a] border-t border-white/5">
      <div className="container-narrow">
        <motion.div {...fadeUp} className="max-w-2xl">
          <p className="text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
            <span className="gold-divider" /> Leadership
          </p>
          <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[1.1]">
            Meet the <span className="italic text-white/85">visionaries</span> behind Seetai.
          </h2>
          <p className="mt-4 text-white/65 text-base md:text-lg leading-relaxed">
            Three directors, one shared conviction — to build townships and assets that
            outlast generations.
          </p>
        </motion.div>

        <div
          className={cn(
            "mt-14 grid gap-7",
            visible.length === 1 && "md:grid-cols-1 max-w-lg mx-auto",
            visible.length === 2 && "md:grid-cols-2 max-w-3xl mx-auto",
            visible.length >= 3 && "md:grid-cols-3"
          )}
        >
          {visible.map((d, i) => (
            <DirectorCard key={d.id || i} director={d} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default DirectorsSection;
